import React, { Component, createRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { TypingIndicator } from "../../components/Icon";
import CoachHeader from "./coach/CoachHeader";
import CoachComposer from "../../components/coach/CoachComposer";
import { urlMap, getAuthHeaders } from "../../../utils";

export default class CoachPanel extends Component {
  inputRef = createRef();
  textareaRef = createRef();

  state = {
    input: "",
    viewMode: "chat", // "chat" | "sessions"
    allSessions: [],
    sessionsPage: 1,
    deletingSessionId: null,
    confirmDeleteSessionId: null,
  };

  SESSIONS_PER_PAGE = 10;
  MAX_INITIAL_SESSIONS = 3;

  componentDidMount() {
    this.inputRef.current?.focus();
  }

  componentDidUpdate(prevProps) {
    // Keep focus on the composer after a message finishes sending
    if (prevProps.sending && !this.props.sending) {
      this.textareaRef.current?.focus();
    }
  }

  onChange = (e) => {
    const value = e.target.value;
    this.setState({ input: value }, this.adjustTextareaHeight);
  };

  adjustTextareaHeight = () => {
    const el = this.textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.handleSend();
    }
  };

  handleSend = () => {
    const { onSend } = this.props;
    const { input } = this.state;
    if (!input.trim()) return;
    
    onSend(input.trim());
    this.setState({ input: "" });
  };

  showSessions = async () => {
    this.setState({ viewMode: "sessions", sessionsPage: 1 });
    if (this.state.allSessions.length === 0) {
      const { chapterId } = this.props;
      if (chapterId) {
        const sessions = await this.fetchSessions(chapterId);
        this.setState({ allSessions: sessions });
      }
    }
  };

  backToChat = () => {
    this.setState({ viewMode: "chat" });
  };

  async fetchSessions(chapterId) {
    try {
      const url = new URL(urlMap.listChatSessions(chapterId), window.location.origin);
      const res = await fetch(url.toString(), {
        method: "GET",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!res.ok) return [];
      return await res.json();
    } catch (err) {
      return [];
    }
  }

  switchSession = (sessionId) => {
    this.props.onSwitchSession?.(sessionId);
    this.setState({ viewMode: "chat" });
  };

  loadMoreSessions = () => {
    this.setState((s) => ({ sessionsPage: s.sessionsPage + 1 }));
  };

  nextSessionsPage = () => {
    this.setState((s) => ({ sessionsPage: s.sessionsPage + 1 }));
  };

  prevSessionsPage = () => {
    this.setState((s) => ({ sessionsPage: Math.max(1, s.sessionsPage - 1) }));
  };

  deleteSession = async (sessionId) => {
    const { chapterId, currentSessionId, onSwitchSession, onReset } = this.props;
    if (!sessionId || !chapterId) return;

    this.setState({ deletingSessionId: sessionId });
    try {
      const res = await fetch(urlMap.deleteChatSession(sessionId), {
        method: "DELETE",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const refreshed = await this.fetchSessions(chapterId);
      const nextSessions = Array.isArray(refreshed) ? refreshed : [];

      this.setState((s) => {
        const totalPages = Math.max(1, Math.ceil(nextSessions.length / this.SESSIONS_PER_PAGE));
        return {
          allSessions: nextSessions,
          sessionsPage: Math.min(s.sessionsPage, totalPages),
        };
      });

      if (currentSessionId === sessionId) {
        if (nextSessions[0]?.id) {
          onSwitchSession?.(nextSessions[0].id);
        } else {
          onReset?.();
        }
      }
    } catch {
      // Keep silent to avoid noisy UI; parent chat area already handles fetch errors.
    } finally {
      this.setState({ deletingSessionId: null, confirmDeleteSessionId: null });
    }
  };

  render() {
    const { 
      messages = [], 
      loading = false, 
      error = null, 
      sending = false,
      typing = false,
      masterModelName = null,
      onClose, 
      onReset,
      seedsUsed = 0,
    } = this.props;

    const {
      input,
      viewMode,
      allSessions,
      sessionsPage,
      deletingSessionId,
      confirmDeleteSessionId,
    } = this.state;

    // Compute session list pagination
    const totalPages = Math.ceil(allSessions.length / this.SESSIONS_PER_PAGE);
    const startIdx = (sessionsPage - 1) * this.SESSIONS_PER_PAGE;
    const endIdx = startIdx + this.SESSIONS_PER_PAGE;
    const visibleSessions = allSessions.slice(startIdx, endIdx);

    const showLoadMore = allSessions.length > this.MAX_INITIAL_SESSIONS && sessionsPage === 1;
    const initialSessions = showLoadMore ? allSessions.slice(0, this.MAX_INITIAL_SESSIONS) : visibleSessions;

    return (
      <div className="coach-scroll">
        <aside className="coach-panel" aria-label="Writing coach chat">
          {/* ── Header with back button if in sessions view ── */}
          <div className="coach-header">
            {viewMode === "sessions" && (
              <button className="btn subtle" onClick={this.backToChat} title="Back to chat">
                ← Back
              </button>
            )}
            {viewMode === "chat" && (
              <CoachHeader
                onReset={onReset}
                onClose={onClose}
                onShowSessions={this.showSessions}
                masterModelName={masterModelName}
              />
            )}
            {viewMode === "sessions" && (
              <>
                <span style={{ flex: 1 }} />
                <button className="btn subtle" onClick={onClose} title="Close coach">
                  ✕
                </button>
              </>
            )}
          </div>

          {/* ── Chat View ── */}
          {viewMode === "chat" && (
            <>
              <div className="coach-messages">
                {loading && <div className="coach-bubble them">Loading…</div>}
                {error && <div className="coach-bubble them">⚠ {error}</div>}
                {messages.map((m) => (
                  <div key={m.id} className={`coach-bubble ${m.from === "me" ? "me" : "them"}`}>
                    <ReactMarkdown className="coach-markdown" remarkPlugins={[remarkGfm]}>
                      {m.text}
                    </ReactMarkdown>
                  </div>
                ))}
                {typing && <TypingIndicator />}
              </div>

              <CoachComposer
                textareaRef={this.textareaRef}
                input={input}
                sending={sending}
                onChange={this.onChange}
                onKeyDown={this.onKeyDown}
                onSend={this.handleSend}
                seedsUsed={seedsUsed}
              />
            </>
          )}

          {/* ── Sessions View ── */}
          {viewMode === "sessions" && (
            <div className="coach-messages coach-sessions-list">
              {allSessions.length === 0 ? (
                <div className="coach-bubble them">No previous sessions</div>
              ) : (
                <>
                  {visibleSessions.map((session) => {
                    const excerpt = session.last_message_preview || session.summary || null;
                    const trimmed = excerpt
                      ? excerpt.replace(/[#*`_~>\[\]]/g, "").trim().slice(0, 80) + (excerpt.length > 80 ? "…" : "")
                      : null;
                    return (
                      <div key={session.id} className="coach-session-item coach-session-row">
                        <button
                          className="coach-session-open"
                          onClick={() => this.switchSession(session.id)}
                        >
                          <span className="coach-session-title">{session.title || "Untitled"}</span>
                          <span className="coach-session-date">
                            {new Date(session.created_at).toLocaleDateString()}
                          </span>
                          {trimmed && (
                            <span className="coach-session-excerpt">{trimmed}</span>
                          )}
                        </button>
                        <button
                          className="btn subtle coach-session-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.setState({ confirmDeleteSessionId: session.id });
                          }}
                          disabled={deletingSessionId === session.id}
                          title="Delete session"
                        >
                          Delete
                        </button>
                        {confirmDeleteSessionId === session.id && (
                          <div className="coach-session-confirm-inline" role="alert">
                            <span className="coach-session-confirm-text">Delete session?</span>
                            <button
                              className="btn subtle coach-session-confirm-btn danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                this.deleteSession(session.id);
                              }}
                              disabled={deletingSessionId === session.id}
                            >
                              {deletingSessionId === session.id ? "Deleting..." : "Confirm"}
                            </button>
                            <button
                              className="btn subtle coach-session-confirm-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                this.setState({ confirmDeleteSessionId: null });
                              }}
                              disabled={deletingSessionId === session.id}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {showLoadMore && (
                    <button
                      className="btn subtle coach-load-more-btn"
                      onClick={this.loadMoreSessions}
                    >
                      Load More
                    </button>
                  )}
                  {!showLoadMore && totalPages > 1 && (
                    <div className="coach-pagination">
                      <button
                        className="btn subtle"
                        onClick={this.prevSessionsPage}
                        disabled={sessionsPage === 1}
                      >
                        ← Prev
                      </button>
                      <span className="coach-pagination-info">
                        Page {sessionsPage} of {totalPages}
                      </span>
                      <button
                        className="btn subtle"
                        onClick={this.nextSessionsPage}
                        disabled={sessionsPage === totalPages}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </aside>
      </div>
    );
  }
}
